import React, { useState } from 'react';

import { Button, Checkbox } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { DataGrid, GridColumnHeaderSortIcon } from '@mui/x-data-grid';
import _ from 'lodash';

import { useSelector } from 'BMapsRedux';
import { UserActions } from 'BMapsCmds';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { App } from 'BMapsSrc/BMapsApp';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { LoadingScreen } from 'BMapsSrc/LoadingScreen';
import { loadProtein } from 'BMapsSrc/project_data';
import { setTargetsTableState } from 'BMapsRedux/projectState/access';
import { userConfirmation } from 'BMapsSrc/utils';
import { CustomColumnsPanel } from 'BMapsUI/tables/CustomColumnsPanel';
import { GridToolBar } from 'BMapsUI/tables/CustomToolbar';
import { Loader } from 'BMapsSrc/Loader';
import { DialogCancelButton, DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { getSelectivityDataConnection, releaseUnusedConnections } from 'BMapsSrc/server/connection_utils';
import { wrapForDisabledTooltip } from 'BMapsUI/common/helpers';

function getDefaultColumnVisibilityModel(targetRows) {
    const columnVisibilityModel = {};
    for (const row of targetRows) {
        for (const [key, value] of Object.entries(row)) {
            if (key === 'id') continue;
            if (key === 'actions') continue;
            columnVisibilityModel[key] = false;
        }
    }
    return {
        ...columnVisibilityModel,
        isTarget: true,
        actions: true,
        isLoaded: true,
        displayName: true,
        pdbID: true,
    };
}

export default function ProjectTargetsTab({ selected, selectivityProps, setConnection }) {
    const { targetList } = useSelector((state) => state.projectState);
    const tableState = useSelector((state) => state.projectState.tableState.targetsTable);
    const loadedStructures = App.Workspace.getLoadedProteins();
    const [selectedRowIds, setSelectedRowIds] = useState([]);

    function setTarget(uri, isTarget) {
        const targetInfo = targetList.find((t) => t.mapCase.uri === uri);
        UserActions.SetProteinTarget(targetInfo.mapCase, { ...targetInfo, isTarget });
    }

    async function loadTarget(mapCaseUris) {
        const uriList = ensureArray(mapCaseUris);
        const integratingStructures = [];
        for (const uri of uriList) {
            const target = targetList.find((t) => t.mapCase.uri === uri);
            if (target) {
                integratingStructures.push(target);
            }
        }
        if (integratingStructures.length === 0) {
            return;
        }
        const mapcasesWithLoadOptions = integratingStructures.map(
            (target) => ({
                mapCase: target.mapCase,
                loadOptions: { keepExisting: true },
                dataConnection: App.PrimaryDataConnection,
                isTarget: target.isTarget,
            })
        );
        await releaseUnusedConnections();
        const loadingNoun = integratingStructures.length === 1
            ? integratingStructures[0].mapCase.displayName
            : `${integratingStructures.length} structures`;
        const loadMsg = `Loading ${loadingNoun} into workspace...`;
        const loadingPromise = LoadingScreen.withLoadingScreen((async () => {
            const errMap = new Map();
            for (const { mapCase, loadOptions, dataConnection } of mapcasesWithLoadOptions) {
                const { error } = await loadProtein({
                    mapCase,
                    loadOptions,
                    dataConnection,
                    loadMsg: `Loading ${mapCase.displayName}...`,
                });
                if (error) errMap.set(mapCase, error);
            }
        })(), loadMsg); // IIFE since withLoadingScreen needs a promise, not a function
        await EventBroker.withIgnoredEvents('escapeKey', loadingPromise);
        if (selectivityProps.selectivity === 'selectivity') {
            const { dataConnection } = await getSelectivityDataConnection();
            setConnection(dataConnection);
        }
        UserActions.SetAllTargetInfo([...targetList]);
    }

    /**
     *
     * @param {string[] | string} uri
     * @param {boolean} removeFromProject
     * @returns
     */
    async function removeFromWorkspace(uri, removeFromProject) {
        const uriList = ensureArray(uri);

        const mapCaseList = [];
        const userCompounds = [];
        for (const mapCaseUri of uriList) {
            const { mapCase } = targetList.find((target) => target.mapCase.uri === mapCaseUri);
            mapCaseList.push(mapCase);
            const cdata = App.Workspace.lookupCaseData(mapCase);
            const userCmpds = cdata ? cdata.getCompounds().filter((c) => !c.isLigand()) : [];
            userCompounds.push(...userCmpds);
        }
        const needPermissionToRemove = removeFromProject || userCompounds.length > 0;
        if (needPermissionToRemove) {
            const noun = uriList.length === 1 ? 'structure' : 'structures';
            const nounPhrase = `${uriList.length === 1 ? 'this' : 'these'} ${noun}`;
            const compoundNoun = userCompounds.length === 1 ? 'compound' : 'compounds';
            const cmpdWarning = userCompounds.length > 0 ? `\n\nThis will permanently remove ${userCompounds.length} user ${compoundNoun} associated with ${nounPhrase}. If you wish to preserve any compounds, export them before proceeding.` : '';
            const verb = removeFromProject ? 'remove' : 'unload';
            const target = removeFromProject ? 'project' : 'workspace';
            const title = `${_.capitalize(verb)} ${_.capitalize(noun)} from ${_.capitalize(target)}?`;
            const clear = await userConfirmation(`Are you sure you want to ${verb} ${nounPhrase} from the ${target}?${cmpdWarning}`, title);
            if (!clear) {
                return;
            }
        }
        await releaseUnusedConnections();
        for (const mapCase of mapCaseList) {
            const caseData = App.Workspace.lookupCaseData(mapCase);
            if (caseData) {
                await EventBroker.withIgnoredEvents('escapeKey', (async () => {
                    await UserActions.RemoveCase(caseData);
                })());
            }
        }
        if (selectivityProps.selectivity === 'selectivity') {
            const { dataConnection } = await getSelectivityDataConnection();
            setConnection(dataConnection);
        }
        if (removeFromProject) {
            const newTargetList = targetList.filter((t) => !uriList.includes(t.mapCase.uri));
            UserActions.SetAllTargetInfo(newTargetList);
        } else {
            // RemoveCase removes the case from the target info. Make sure it is added back.
            UserActions.SetAllTargetInfo([...targetList]);
        }
    }

    const {
        columns,
        targetRows,
    } = buildTargetsGrid(
        targetList, loadedStructures, loadTarget, removeFromWorkspace, setTarget
    );

    return (
        <>
            {!!selected && (
                <DialogContentSection
                    title="Project Targets"
                >
                    <DataGrid
                        rows={targetRows}
                        columns={columns}
                        disableRowSelectionOnClick
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 },
                            },
                        }}
                        pageSizeOptions={[5, 10, 20]}
                        onRowSelectionModelChange={(rowIds) => {
                            setSelectedRowIds(rowIds);
                        }}
                        onColumnVisibilityModelChange={(columnVisibilityModel) => {
                            setTargetsTableState({ columnVisibilityModel });
                        }}
                        columnVisibilityModel={tableState?.columnVisibilityModel
                            || getDefaultColumnVisibilityModel(targetRows)}
                        slots={{
                            toolbar: (toolBarProps) => (
                                <GridToolBar
                                    {...toolBarProps}
                                    useFilter
                                    exportFileName="Project-Targets"
                                    toolBarId="targetStructuresTable"
                                />
                            ),
                            columnsPanel: CustomColumnsPanel,
                            columnHeaderSortIcon: (props) => {
                                const found = columns.find((c) => c.field === props.field);
                                const title = found ? `Sort by ${found.headerName}` : 'Sort';
                                return (
                                    <BMapsTooltip title={title} noMaxWidth>
                                        <GridColumnHeaderSortIcon {...props} />
                                    </BMapsTooltip>
                                );
                            },
                        }}
                        showToolbar
                        checkboxSelection
                    />

                    <TableSelectionButtons
                        selectedRowIds={selectedRowIds}
                        loadTarget={loadTarget}
                        removeFromWorkspace={removeFromWorkspace}
                        targetRows={targetRows}
                    />
                </DialogContentSection>
            )}

        </>
    );
}

function TableSelectionButtons({
    selectedRowIds, loadTarget, removeFromWorkspace, targetRows,
}) {
    const selectedItems = targetRows.filter((r) => selectedRowIds.includes(r.id));
    const [loadedStructures, unloadedStructures] = _.partition(selectedItems, (r) => r.isLoaded);

    const disableLoadButton = unloadedStructures.length < 1;
    const disableUnloadButton = loadedStructures.length < 1;
    const disableRemoveButton = selectedItems.length < 1;
    const disableClearProjectButton = targetRows.length < 1;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            gap: '1rem',
            paddingTop: '1rem',
            width: window.screen.width > 1000 ? '24rem' : '100%',
        }}
        >
            <BMapsTooltip
                title={disableLoadButton
                    ? 'Select an unloaded structure to load into the workspace'
                    : 'Load selected structures into the workspace'}
            >
                {wrapForDisabledTooltip(
                    <DialogPrimaryButton
                        onClick={() => {
                            const uriList = unloadedStructures
                                .map((item) => item.uri);
                            loadTarget(uriList);
                        }}
                        sx={{ width: '100%' }}
                        disabled={disableLoadButton}
                    >
                        Load Selected
                    </DialogPrimaryButton>
                )}
            </BMapsTooltip>
            <BMapsTooltip
                title={disableUnloadButton
                    ? 'Select a loaded structure to unload from the workspace'
                    : 'Unload selected structures from the workspace'}
            >
                {wrapForDisabledTooltip(
                    <DialogCancelButton
                        onClick={() => {
                            const uriList = loadedStructures
                                .map((item) => item.uri);
                            removeFromWorkspace(uriList);
                        }}
                        sx={{ width: '100%' }}
                        disabled={disableUnloadButton}
                    >
                        Unload Selected
                    </DialogCancelButton>
                )}
            </BMapsTooltip>
            <BMapsTooltip
                title={disableRemoveButton
                    ? 'Select a structure to remove from the workspace and from the project targets'
                    : 'Remove selected structures from the workspace and from the project targets'}
            >
                {wrapForDisabledTooltip(
                    <DialogCancelButton
                        onClick={() => {
                            const uriList = selectedItems.map((item) => item.uri);
                            removeFromWorkspace(uriList, true);
                        }}
                        sx={{ width: '100%' }}
                        disabled={disableRemoveButton}
                    >
                        Remove Selected From The Project
                    </DialogCancelButton>
                )}
            </BMapsTooltip>
            <BMapsTooltip
                title={disableClearProjectButton
                    ? 'No structures or project info to remove'
                    : 'Remove all structures from the workspace and from the project targets'}
            >
                {wrapForDisabledTooltip(
                    <Button
                        variant="contained"
                        color="error"
                        onClick={
                            () => {
                                const uriList = targetRows.map((item) => item.uri);
                                removeFromWorkspace(uriList, true);
                            }
                        }
                        disabled={disableClearProjectButton}
                        sx={{ width: '100%' }}
                    >
                        Clear Project
                    </Button>
                )}
            </BMapsTooltip>
        </Box>
    );
}

function buildTargetsGrid(
    targetList, loadedStructures, loadTarget, removeFromWorkspace, setTarget
) {
    const columnNames = [
        'displayName', 'pdbID', 'isTarget', 'isLoaded',
        'organism', 'description',
        'gene_name', 'sourceDesc', 'lastUpdate',
        'dataSource',
    ];
    if (Loader.AllowLabFeatures) {
        columnNames.push('uri', 'project', 'case', 'projectCase');
    }

    // add scopedProperties fields
    for (const target of targetList) {
        const { mapCase } = target;
        const properties = mapCase.listProperties({ includeChildren: true });
        for (const property of properties) {
            if (!columnNames.includes(property.name)) {
                columnNames.push(property.name);
            }
        }
    }

    // add otherProperties fields
    for (const target of targetList) {
        const { mapCase } = target;
        if (!mapCase.otherProperties) continue;
        for (const [key, value] of Object.entries(mapCase.otherProperties)) {
            if (!columnNames.includes(key)) {
                columnNames.push(key);
            }
        }
    }
    columnNames.push('actions');

    const columns = columnNames.map((name) => {
        switch (name) {
            case 'displayName':
                return {
                    field: name,
                    headerName: 'Name',
                    title: 'Name',
                    flex: 2,
                };
            case 'isTarget':
                return {
                    field: name,
                    headerName: 'Is Target',
                    title: 'Is Target',
                    flex: 1,
                    renderCell: ({ row }) => (
                        <Checkbox
                            checked={row.isTarget === true}
                            onClick={() => {
                                setTarget(row.id, !row.isTarget);
                            }}
                        />
                    ),
                };
            case 'isLoaded':
                return {
                    field: name,
                    headerName: 'Workspace',
                    title: 'Workspace',
                    flex: 1,
                    renderCell: ({ row }) => (
                        <>
                            {row.isLoaded ? 'Loaded' : 'Not loaded'}
                        </>
                    ),
                };
            case 'actions':
                return {
                    field: name,
                    headerName: 'Actions',
                    title: 'Actions',
                    flex: 1,
                    minWidth: 75,
                    sortable: false,
                    filterable: false,
                    align: 'right',
                    headerAlign: 'right',
                    renderCell: ({ row }) => {
                        const actions = [];
                        if (row.isLoaded) {
                            actions.push(
                                <BMapsTooltip title="Unload this structure from the workspace">
                                    <IconButton
                                        size="small"
                                        onClick={() => removeFromWorkspace(row.id)}
                                    >
                                        <RemoveCircleOutlineIcon fontSize="inherit" />

                                    </IconButton>
                                </BMapsTooltip>
                            );
                        } else {
                            actions.push(
                                <BMapsTooltip title="Load this structure into the workspace">
                                    <IconButton
                                        size="small"
                                        onClick={() => loadTarget(row.id)}
                                    >
                                        <AddCircleOutlineIcon fontSize="inherit" />
                                    </IconButton>
                                </BMapsTooltip>
                            );
                        }
                        actions.push(
                            <BMapsTooltip title="Remove this structure from the workspace and the project">
                                <IconButton
                                    size="small"
                                    onClick={() => removeFromWorkspace(row.id, true)}
                                >
                                    <ClearIcon fontSize="inherit" />
                                </IconButton>
                            </BMapsTooltip>,
                        );
                        return (
                            <Box>
                                {actions.map((a) => (
                                    // Actions are BMapsTooltips with unique titles
                                    <React.Fragment key={a.props.title}>{a}</React.Fragment>
                                ))}
                            </Box>
                        );
                    },
                };
            case 'lastUpdate': {
                return {
                    field: name,
                    headerName: 'Last Update',
                    title: 'Last Update',
                    minWidth: 120,
                    renderCell: ({ value, row }) => {
                        if (!value) return null;
                        const displayValue = new Date(value).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                        });
                        const tooltip = displayValue;
                        return (
                            <BMapsTooltip title={tooltip}>
                                <span style={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                >
                                    {displayValue}
                                </span>
                            </BMapsTooltip>
                        );
                    },
                };
            }
            case 'sourceDesc': {
                return {
                    field: name,
                    headerName: 'Source',
                    title: 'Source',
                    flex: 1,
                };
            }
            case 'pdbID': {
                return {
                    field: name,
                    headerName: 'PDB ID',
                    title: 'PDB ID',
                    flex: 1,
                };
            }
            case 'gene_name': {
                return {
                    field: name,
                    headerName: 'Gene Name',
                    title: 'Gene Name',
                    flex: 1,
                };
            }
            case 'dataSource': {
                return {
                    field: name,
                    headerName: 'Data Source',
                    title: 'Data Source',
                    flex: 1,
                };
            }
            case 'molecule_name': {
                return {
                    field: name,
                    headerName: 'Molecule Name',
                    title: 'Molecule Name',
                    flex: 1,
                };
            }
            case 'projectCase': {
                return {
                    field: name,
                    headerName: 'Project Case',
                    title: 'Project Case',
                    flex: 1,
                };
            }
            case 'uri': {
                return {
                    field: name,
                    headerName: 'URI',
                    title: 'URI',
                    flex: 1,
                };
            }

            default:
                return {
                    field: name, headerName: name, title: name, flex: 1,
                };
        }
    });
    const targetRows = _.uniqBy(targetList, (t) => t.mapCase.uri).map((target) => {
        const row = {
            ...target.mapCase.otherProperties,
            ...target.mapCase
                .listProperties({ includeChildren: true }).map((p) => ({ [p.name]: p.value })),
            id: target.mapCase.uri,
            displayName: target.mapCase.displayName,
            isTarget: target.isTarget,
            isLoaded: loadedStructures.includes(target.mapCase),
            pdbID: target.mapCase.pdbID,
            description: target.mapCase.description,
            molecule_name: target.mapCase.molecule_name,
            gene_name: target.mapCase.gene_name,
            lastUpdate: target.mapCase.lastUpdate,
            organism: target.mapCase.organism,
            sourceDesc: target.mapCase.sourceDesc,
            data_format: target.mapCase.data_format,
            dataSource: target.mapCase.dataSource?.name,
        };
        if (Loader.AllowDevFeatures) {
            row.projectCase = target.mapCase.projectCase;
            row.case = target.mapCase.case;
            row.project = target.mapCase.project;
            row.uri = target.mapCase.uri;
        }
        return row;
    })
        .filter((t) => t.id);
    if (targetRows.length === 0) {
        return { targetRows: [], columns: [] };
    }
    return {
        targetRows, columns,
    };
}
